import {
  type GridPosition,
  type InteractionSource,
  type SelectionRange,
  type SelectionState,
} from '../../../state/types';
import { type ReducerResponse, type SelectionAxis } from '../../types';
import { getRangeMinMax } from '../range/get-range-min-max';

type ShortenRangeAtBoundaryParams = {
  state: SelectionState;
  range: SelectionRange;
  index: number;
  source: InteractionSource;
  axis: SelectionAxis;
};

/**
 * Shortens a range by removing an item at the start or end boundary.
 * @example
 * // User has rows 2-5 selected, clicks row 5 (end boundary) to deselect it
 * // Before: selections = [{ id: 'r1', anchor: { row: 2, col: 0 }, lead: { row: 5, col: 0 } }]
 * // After: selections = [{ id: 'r1', anchor: { row: 2, col: 0 }, lead: { row: 4, col: 0 } }]
 *
 * @returns A reducer response with updated state and delta
 */
export function shortenRangeAtBoundary({
  state,
  range,
  index,
  source,
  axis,
}: ShortenRangeAtBoundaryParams): ReducerResponse {
  const { min, max } = getRangeMinMax(range, axis);
  const anchorAtStart = range.anchor[axis].index === min;
  const startPosition = anchorAtStart ? range.anchor : range.lead;
  const endPosition = anchorAtStart ? range.lead : range.anchor;

  const newMin = index === min ? min + 1 : min;
  const newMax = index === max ? max - 1 : max;

  const newStart: GridPosition =
    axis === 'row'
      ? { row: { index: newMin }, col: startPosition.col }
      : { row: startPosition.row, col: { index: newMin } };

  const newEnd: GridPosition =
    axis === 'row'
      ? { row: { index: newMax }, col: endPosition.col }
      : { row: endPosition.row, col: { index: newMax } };

  const updatedRange: SelectionRange = {
    id: range.id,
    anchor: anchorAtStart ? newStart : newEnd,
    lead: anchorAtStart ? newEnd : newStart,
  };

  const updatedSelections = state.selections.map((r) =>
    r.id === range.id ? updatedRange : r
  );

  const targetMode = axis === 'row' ? 'row' : 'column';

  return {
    delta: {
      kind: 'selectionModified',
      source,
      updated: [updatedRange],
      focusChange: {
        prev: state.focusedCell,
        next: null,
      },
    },
    next: {
      ...state,
      mode: targetMode,
      selections: updatedSelections,
      focusedCell: null,
      lastInteractionSource: source,
    },
  };
}
